import React from "react";
import { Pressable, View } from "react-native";

import { Text } from "./ui/BrandText";
import { text as uiText } from "../theme/tokens";
import {
  getAuthorIdentityId,
  getPublicAuthorSnapshot,
} from "../lib/identity";
import { getSubjectLabel, getTrackLabel } from "../lib/taxonomy";
import type { FirePost } from "../types/models";
import { IdentityHeader } from "./IdentityHeader";

function toMillis(value: any): number {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
}

function formatPostDate(value: any) {
  const millis = toMillis(value);
  if (!millis) return "";

  return new Date(millis).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function PostAuthorHeader({
  post,
  onAuthorPress,
  onGroupPress,
  disableAuthorLink = false,
  disableGroupLink = false,
}: {
  post: FirePost;
  onAuthorPress?: (identityId: string) => void;
  onGroupPress?: (groupId: string) => void;
  disableAuthorLink?: boolean;
  disableGroupLink?: boolean;
}) {
  const identity = getPublicAuthorSnapshot(post) ?? {
    identityId: getAuthorIdentityId(post) ?? post.authorUid,
    handle: "",
    displayName: "Utilisateur",
    avatarUrl: null,
    accountType: "student" as const,
    badges: [],
  };
  const subject = post.subject && post.subject !== "general" ? getSubjectLabel(post.subject) : "";
  const track = post.track ? getTrackLabel(post.track) : "";
  const groupName = post.visibility === "group" && post.groupName ? post.groupName : "";
  const identityId = identity.identityId || post.authorUid;
  const dateLabel = formatPostDate(post.createdAt);
  const academicContext = [subject, track].filter(Boolean).join(" · ");
  const groupLabel = groupName ? (
    <>
      <Text style={{ color: uiText.muted, fontSize: 12 }}>|</Text>
      {onGroupPress && post.groupId && !disableGroupLink ? (
        <Pressable
          onPress={() => onGroupPress(post.groupId!)}
          accessibilityRole="button"
          accessibilityLabel={`Ouvrir le groupe ${groupName}`}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1, maxWidth: "100%" })}
        >
          <Text style={{ color: uiText.secondary, fontWeight: "800", fontSize: 13, flexShrink: 1 }}>
            {groupName}
          </Text>
        </Pressable>
      ) : (
        <Text style={{ color: uiText.muted, fontWeight: "800", fontSize: 13, flexShrink: 1 }}>
          {groupName}
        </Text>
      )}
    </>
  ) : null;

  return (
    <View style={{ gap: 2, minWidth: 0 }}>
      <IdentityHeader
        identity={identity}
        compact
        showHandle={false}
        pressableIdentityOnly
        nameTrailingContent={groupLabel}
        disabled={disableAuthorLink || !identityId}
        onPress={onAuthorPress && identityId ? () => onAuthorPress(identityId) : undefined}
      />

      <View style={{ paddingLeft: 50, gap: 3, minWidth: 0 }}>
        {academicContext || dateLabel ? (
          <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 5, minWidth: 0 }}>
            {academicContext ? (
              <Text style={{ color: uiText.secondary, fontWeight: "800", fontSize: 12, flexShrink: 1 }}>
                {academicContext}
              </Text>
            ) : null}
            {academicContext && dateLabel ? <Text style={{ color: uiText.muted, fontSize: 12 }}>·</Text> : null}
            {dateLabel ? <Text style={{ color: uiText.muted, fontSize: 12 }}>{dateLabel}</Text> : null}
          </View>
        ) : null}
      </View>
    </View>
  );
}
